import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Tooltip, IconButton, Button, Modal, Box, Typography, TextareaAutosize, List, ListItem, TextField, CircularProgress, Grid, Tabs, Tab, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Alert } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CampaignIcon from '@mui/icons-material/Campaign';
import PreviewIcon from '@mui/icons-material/Visibility';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import BugReportIcon from '@mui/icons-material/BugReport';
import CodeIcon from '@mui/icons-material/Code';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScienceIcon from '@mui/icons-material/Science';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { debounce } from 'lodash';
import prettier from "prettier/standalone";
import * as parserHtml from "prettier/parser-html";
import Collapse from '@mui/material/Collapse';
import { Dialog, DialogActions, DialogContent, DialogTitle, Autocomplete } from '@mui/material';

import { api } from '../../services/auth';
import spamServices from '../../services/spamservices';
import { getUserConfig } from '../../services/config';

const EmailPreviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { email } = location.state || {};
  const [associatedCampaigns, setAssociatedCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);
  const [modal, setModal] = useState(null);
  const [sourceContent, setSourceContent] = useState('');
  const [sourceTitle, setSourceTitle] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [spamModalOpen, setSpamModalOpen] = useState(false);
  const [spamFormData, setSpamFormData] = useState({ from: '', to: '', subject: '' });
  const [spamReport, setSpamReport] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSpamReportLoading, setIsSpamReportLoading] = useState(false);
  const [spamError, setSpamError] = useState(null);
  const [mailPreviewModalOpen, setMailPreviewModalOpen] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [testId, setTestId] = useState(null);
  const [previewResults, setPreviewResults] = useState([]);
  const [isPolling, setIsPolling] = useState(false);
  const [isSendTestModalOpen, setIsSendTestModalOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  // State for the new Send Test Modal
  const [testRecipients, setTestRecipients] = useState([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [contactInput, setContactInput] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [onGoingAbTests, setOnGoingAbTests] = useState([]);
  const [loadingAbTestStatus, setLoadingAbTestStatus] = useState(true);

  const UNOMI_BASE_URL = process.env.REACT_APP_UNOMI_BASE_URL;
  const AUTH_TOKEN = process.env.REACT_APP_UNOMI_AUTH_TOKEN;

  useEffect(() => {
    const fetchAbTestStatus = async () => {
      try {
        const response = await api.get('/abtest_notify/');
        if (response.data && Array.isArray(response.data)) {
          const onGoingIds = response.data
            .flatMap(test => [test.parent_email_id, test.abtest_email_id])
            .filter(id => id != null);
          const uniqueOnGoingIds = [...new Set(onGoingIds)];
          setOnGoingAbTests(uniqueOnGoingIds);
        }
      } catch (error) {
        console.error("A/B test status refresh failed:", error);
      } finally {
        setLoadingAbTestStatus(false);
      }
    };

    fetchAbTestStatus();
    const intervalId = setInterval(fetchAbTestStatus, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const searchContacts = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setContactOptions([]);
      return;
    }
    setContactsLoading(true);
    try {
      const response = await fetch(`${UNOMI_BASE_URL}/cxs/profiles/search`, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${AUTH_TOKEN}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          "condition": {
            "type": "booleanCondition",
            "parameterValues": {
              "operator": "and",
              "subConditions": [
                {
                  "type": "profilePropertyCondition",
                  "parameterValues": {
                    "propertyName": "properties.brand",
                    "comparisonOperator": "equals",
                    "propertyValue": getUserConfig().brand
                  }
                },
                {
                  "type": "booleanCondition",
                  "parameterValues": {
                    "operator": "or",
                    "subConditions": [
                      {
                        "type": "profilePropertyCondition",
                        "parameterValues": {
                          "propertyName": "properties.firstName",
                          "comparisonOperator": "contains",
                          "propertyValue": searchTerm
                        }
                      },
                      {
                        "type": "profilePropertyCondition",
                        "parameterValues": {
                          "propertyName": "properties.lastName",
                          "comparisonOperator": "contains",
                          "propertyValue": searchTerm
                        }
                      },
                      {
                        "type": "profilePropertyCondition",
                        "parameterValues": {
                          "propertyName": "properties.email",
                          "comparisonOperator": "contains",
                          "propertyValue": searchTerm
                        }
                      }
                    ]
                  }
                }
              ]
            }
          },
          "limit": 10,
          "sortby": "properties.lastName:asc"
        })
      });
      const data = await response.json();
      setContactOptions(data.list || []);
    } catch (error) {
      console.error("Erreur lors de la recherche des contacts:", error);
      setContactOptions([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const debouncedSearch = debounce(searchContacts, 300);

  useEffect(() => {
    if (!email) {
      setCampaignsLoading(false);
      return;
    }

    const findCampaigns = async () => {
      setCampaignsLoading(true);
      try {
        const response = await api.get('/campaigns');
        const allCampaigns = response.data.campaigns || [];
        
        const relatedCampaigns = allCampaigns.filter(campaign => 
          campaign.events.some(event => 
            event.type === 'email.send' && parseInt(event.properties?.email, 10) === parseInt(email.id, 10)
          )
        );
        setAssociatedCampaigns(relatedCampaigns);
      } catch (error) {
        console.error("Erreur lors de la recherche des campagnes associées:", error);
        setAssociatedCampaigns([]); // En cas d'erreur, on n'affiche rien
      } finally {
        setCampaignsLoading(false);
      }
    };

    findCampaigns();
  }, [email]);

  // Add function to open mail preview modal
  const openMailPreviewModal = () => {
    setMailPreviewModalOpen(true);
    sendForPreview(); // Kick off the test submission
  };

  // 1. Submits the test to the backend
  const sendForPreview = async () => {
    setIsPreviewLoading(true);
    setPreviewError(null);
    setTestId(null);
    setPreviewResults([]);

    try {
      const response = await api.post('/emails/send-preview', {
        subject: email.name || 'Email Preview',
        html: email.customHtml || email.html,
      });

      if (response.data.success && response.data.test_id) {
        setTestId(response.data.test_id);
        setIsPolling(true); // Start polling for results
      } else {
        setPreviewError(response.data.message || 'Failed to submit email test.');
        setIsPreviewLoading(false);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'An error occurred while submitting the test.';
      setPreviewError(errorMessage);
      setIsPreviewLoading(false);
    }
  };

  // 2. Polls for results using the testId
  useEffect(() => {
    if (!isPolling || !testId) return;

    const interval = setInterval(async () => {
      try {
        const response = await api.get(`/emails/send-preview/${testId}/results`);
        if (response.data.success) {
          const results = response.data.results;
          setPreviewResults(results);

          // Check if all clients are done processing
          const allComplete = results.every(r => r.status === 'Complete' || r.status === 'Bounced');
          if (allComplete) {
            setIsPolling(false);
            setIsPreviewLoading(false);
            clearInterval(interval);
          }
        }
      } catch (error) {
        console.error('Error polling for results:', error);
        setPreviewError('Failed to fetch preview results.');
        setIsPolling(false);
        setIsPreviewLoading(false);
        clearInterval(interval);
      }
    }, 5000); // Poll every 5 seconds

    // Cleanup on component unmount or when polling stops
    return () => clearInterval(interval);
  }, [isPolling, testId]);

  const openModal = async (type) => {
    setSourceTitle(type === 'html' ? 'Code Source HTML' : 'Version Texte');
    
    if (type === 'html') {
      const rawHtml = email?.customHtml || email?.html || 'Contenu HTML non disponible.';
      try {
        let formattedHtml = await prettier.format(rawHtml, {
          parser: "html",
          plugins: [parserHtml],
          printWidth: 120, // Ajuster la largeur pour une meilleure lisibilité
        });
       
        setSourceContent(formattedHtml);
      } catch (error) {
        console.error("Erreur de formatage HTML avec Prettier:", error);
        setSourceContent(rawHtml); // En cas d'erreur, afficher le code brut
      }
    } else {
      setSourceContent(email?.plainText || 'Version texte non disponible.');
    }

    setModal('source');
  };

  const handleCopy = () => {
    if (!sourceContent) return;
    navigator.clipboard.writeText(sourceContent).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000); // Reset after 2 seconds
    }).catch(err => {
      console.error('Erreur lors de la copie du texte: ', err);
    });
  };

  // Handler to open the spam modal and fetch the latest report
  const openSpamModal = async () => {
    setSpamModalOpen(true);
    setSpamError(null);
    setIsSpamReportLoading(true);
    setSpamReport(null);
    setSpamFormData({
      from: 'contact@qilinsa.com',
      subject: email?.subject || '',
    });
    // Use subject as emailId (or fallback to id)
    const emailId = email?.subject || email?.id?.toString() || '';
    try {
      const report = await spamServices.getLatestSpamReport(emailId);
      if (report) {
        setSpamReport(report);
      } else {
        setSpamReport(null);
      }
    } catch (err) {
      setSpamReport(null);
      setSpamError('Erreur lors de la récupération du rapport anti-spam.');
    } finally {
      setIsSpamReportLoading(false);
    }
  };

  // Handler for running a new analysis
  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    setSpamError(null);
    try {
      const emailData = {
        htmlContent: email.html || email.customHtml || '',
        from: spamFormData.from,
        to: spamFormData.to,
        subject: spamFormData.subject
      };
      const report = await spamServices.checkSpam(emailData);
      setSpamReport(report);
    } catch (error) {
      setSpamError(error.message || 'Failed to analyze email. Please try again later.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const emailIframeStyle = {
    width: isMobileView ? 375 : '100%',
    height: 600,
    border: 'none',
    display: 'block', // Nécessaire pour que margin: auto fonctionne
    margin: isMobileView ? '0 auto' : '0',
  };

  const roundedButtonStyle = {
    borderRadius: 12,
    border: '1px solid #ccc',
    backgroundColor: '#f4f4f4',
    width: 40,
    height: 40,
    minWidth: 40,
    marginLeft: 1,
    '&:hover': { backgroundColor: '#e0e0e0' },
  };

  if (!email) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="80vh"><Typography>Aucun email trouvé.</Typography></Box>;
  }

  return (
    <div style={{ padding: 20, backgroundColor: '#f4f7fc', fontFamily: 'Segoe UI' }}>
      <div style={{ maxWidth: 1000, margin: 'auto', backgroundColor: '#fff', padding: 30, borderRadius: 10, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #eee', paddingBottom: 15 }}>
          <Box display="flex" alignItems="center">
            <Tooltip title="Retour">
              <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h6" display="flex" alignItems="center" gap={1}>
              Prévisualisation : {email.name}
              {(email?.variantChildren?.length > 0 || email?.variantParent) && (
                loadingAbTestStatus ? (
                  <CircularProgress size={20} sx={{ ml: 1 }} />
                ) : (
                  <Tooltip title="Consulter les résultats du test A/B">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/abtest-results/${email.id}?raw=1`, { state: { emailName: email.name } })}
                      sx={{
                        ml: 1,
                        color: onGoingAbTests.includes(email.id) ? 'success.main' : 'grey.600',
                        border: '1px solid',
                        borderColor: onGoingAbTests.includes(email.id) ? 'success.light' : 'grey.400',
                        backgroundColor: 'transparent',
                        '&:hover': {
                          backgroundColor: onGoingAbTests.includes(email.id) ? 'rgba(211, 47, 47, 0.04)' : 'rgba(46, 125, 50, 0.04)',
                        },
                      }}
                    >
                      <ScienceIcon />
                    </IconButton>
                  </Tooltip>
                )
              )}
            </Typography>
          </Box>
          <div>
            <Tooltip title="Envoyer un Test">
              <IconButton sx={{ ...roundedButtonStyle, backgroundColor: '#3498db', color: 'white' }} onClick={() => setIsSendTestModalOpen(true)}>
                <SendIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Modifier l'Email">
              <IconButton sx={{ ...roundedButtonStyle, backgroundColor: '#f1c40f', color: '#333' }} onClick={() => navigate(`/emails/${id}`, { state: { email } })}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            
          </div>
        </div>

        <div style={{ backgroundColor: '#f8f9fa', padding: 15, borderRadius: 6, marginTop: 20, marginBottom: 25 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 15 }}>
            <div><strong>Objet :</strong> {email.subject || '—'}</div>
            <div><strong>Nom Interne :</strong> {email.name || '—'}</div>
            <div><strong>Type :</strong> {email.emailType || 'Standard'}</div>
            <div><strong>Statut :</strong> <span style={{ color: email.isPublished ? 'green' : 'orange', fontWeight: 'bold' }}>{email.isPublished ? 'Publié' : 'Brouillon'}</span></div>
            <div><strong>Dernière Modification :</strong> {email.dateModified ? new Date(email.dateModified).toLocaleString('fr-FR') : '—'} par {email.modifiedByUser || 'N/A'}</div>
            <div><strong>Créé par :</strong> {email.createdByUser || '—'}</div>
            <div><strong>Langue :</strong> {email.language || 'Toutes'}</div>
            <div>
              <strong>Utilisé dans :</strong>
              {campaignsLoading ? (
                <CircularProgress size={16} sx={{ ml: 1 }} />
              ) : associatedCampaigns.length > 0 ? (
                <List>
                  {associatedCampaigns.map((campaign) => (
                    <ListItem key={campaign.id} sx={{ pl: 1 }}>
                      <Link 
                        to={`/campagne?highlight=${campaign.id}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <Typography color="primary" sx={{ '&:hover': { textDecoration: 'underline' } }}>
                          {campaign.name}
                        </Typography>
                      </Link>
                    </ListItem>
                  ))}
                </List>
              ) : (
                ' Aucune campagne'
              )}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
          <Tooltip title="Prévisualiser Clients Messagerie">
            <IconButton sx={roundedButtonStyle} onClick={openMailPreviewModal}>
              <PreviewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Consulter La Dernière Analyse Anti-Spam"><IconButton sx={roundedButtonStyle} onClick={openSpamModal}><BugReportIcon /></IconButton></Tooltip>
          <Tooltip title="Voir Code HTML"><IconButton sx={roundedButtonStyle} onClick={() => openModal('html')}><CodeIcon /></IconButton></Tooltip>
          <Tooltip title="Voir Version Texte"><IconButton sx={roundedButtonStyle} onClick={() => openModal('text')}><TextSnippetIcon /></IconButton></Tooltip>
        </div>

        <Box textAlign="right" mb={2}>
          <Tooltip title="Vue Desktop">
            <IconButton
              onClick={() => setIsMobileView(false)}
              sx={{ m: 0.5, border: '1px solid', borderColor: 'primary.main', bgcolor: !isMobileView ? 'primary.main' : 'transparent', color: !isMobileView ? 'white' : 'primary.main', '&:hover': { bgcolor: !isMobileView ? 'primary.dark' : 'action.hover' } }}
            >
              <DesktopWindowsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Vue Mobile">
            <IconButton
              onClick={() => setIsMobileView(true)}
              sx={{ m: 0.5, border: '1px solid', borderColor: 'primary.main', bgcolor: isMobileView ? 'primary.main' : 'transparent', color: isMobileView ? 'white' : 'primary.main', '&:hover': { bgcolor: isMobileView ? 'primary.dark' : 'action.hover' } }}
            >
              <SmartphoneIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <div style={{ border: '1px solid #ccc', borderRadius: 5, overflow: 'hidden' }}>
          <iframe
            title="Email Preview"
            srcDoc={(email.customHtml || email.html || '<body>Contenu indisponible.</body>').replace(/<a\s+(?:[^>]*?\s+)?href=/gi, '<a target="_blank" $&')}
            style={emailIframeStyle}
          />
        </div>
      </div>

      {/* Spam Analysis Modal */}
      <Modal open={spamModalOpen} onClose={() => setSpamModalOpen(false)}>
        <Box sx={{ position: 'relative', backgroundColor: 'white', borderRadius: 2, p: 4, width: 'clamp(400px, 60vw, 800px)', margin: '5vh auto', outline: 'none', maxHeight: '90vh', overflowY: 'auto' }}>
          <IconButton
            aria-label="close"
            onClick={() => setSpamModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom>Analyse Anti-Spam</Typography>
          
          {/* Removed TextField inputs for 'from', 'to', and 'subject' */}

          <Button 
            variant="contained" 
            onClick={handleAnalyze} 
            disabled={isAnalyzing || !spamFormData.from || !spamFormData.subject}
            startIcon={isAnalyzing ? <CircularProgress size={20} color="inherit" /> : <BugReportIcon />}
            fullWidth
            sx={{ my: 3 }}
          >
            {isAnalyzing ? 'Analyse en cours...' : 'Analyser'}
          </Button>

          {spamError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {spamError}
            </Alert>
          )}

          {/* Show message if no spam report is found and not loading */}
          {(!isSpamReportLoading && !spamReport) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Aucune dernière analyse anti-spam trouvée. Cliquez sur le bouton 'Analyser' pour générer un rapport anti-spam.
            </Alert>
          )}

          {isSpamReportLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Chargement du rapport anti-spam...</Typography>
            </Box>
          )}

          {spamReport && (
            <Box sx={{ borderTop: '1px solid #ddd', pt: 2 }}>
              {/* Score global et verdict - NEW STYLE */}
              <Box mb={3} sx={{ textAlign: 'center', py: 2 }}>
                {/* Score circle with color */}
                <div
                  style={{
                    width: 150,
                    height: 150,
                    margin: '0 auto 20px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 52,
                    fontWeight: 'bold',
                    color: 'white',
                    boxShadow: '0 5px 15px rgba(0,0,0,0.15)',
                    backgroundColor:
                      spamReport.score >= 9
                        ? '#27ae60' // green
                        : spamReport.score >= 7
                        ? '#f39c12' // orange
                        : spamReport.score >= 4
                        ? '#d35400' // dark orange
                        : spamReport.score >= 2
                        ? '#c0392b' // red
                        : '#8e0e00', // dark red
                  }}
                >
                  {spamReport.score} <span style={{ fontSize: 28, fontWeight: 400, marginLeft: 6 }}>/ 10</span>
                </div>
                {/* Verdict title */}
                <div style={{ fontSize: 28, fontWeight: 700, margin: '10px 0 8px 0' }}>
                  {spamReport.verdict}
                </div>
                {/* Summary text */}
                <div style={{ maxWidth: 600, margin: '0 auto', color: '#555', fontSize: 18, lineHeight: 1.5 }}>
                  {spamReport.verdictDesc || spamReport.verdict_desc}
                </div>
              </Box>

              {/* LLM Report (detailed AI analysis) */}
              {spamReport.sections && spamReport.sections.length > 0 && (
                <Box>
                  {spamReport.sections.map((section, idx) => {
                    // Skip the "tech" section as requested
                    if (section.key === 'tech') return null;
                    
                    const isOpen = openSection === section.key;
                    // Extract the relevant part of the LLM report for this section
                    let sectionLLMReport = '';
                    const llmReportText = spamReport.llm_report || spamReport.llmReport || '';
                    const sectionHeaders = {
                      'auth': "1. Analyse de l'Authentification",
                      'reputation': '2. Analyse de la Réputation',
                      'content': '3. Analyse du Contenu et de la Sécurité',
                      'status': '4. Synthèse de l\'Analyse'
                    };
                    if (llmReportText && sectionHeaders[section.key]) {
                      const sectionHeader = sectionHeaders[section.key];
                      // Find the next section header (if any)
                      const headerKeys = Object.keys(sectionHeaders);
                      const currentIdx = headerKeys.indexOf(section.key);
                      let nextSectionHeader = null;
                      for (let i = currentIdx + 1; i < headerKeys.length; i++) {
                        if (sectionHeaders[headerKeys[i]]) {
                          nextSectionHeader = sectionHeaders[headerKeys[i]];
                          break;
                        }
                      }
                      let startIdx = llmReportText.indexOf(sectionHeader);
                      if (startIdx !== -1) {
                        startIdx = startIdx + sectionHeader.length;
                        let endIdx = nextSectionHeader ? llmReportText.indexOf(nextSectionHeader) : llmReportText.length;
                        if (endIdx !== -1 && endIdx > startIdx) {
                          sectionLLMReport = llmReportText.substring(startIdx, endIdx).trim();
                        } else {
                          sectionLLMReport = llmReportText.substring(startIdx).trim();
                        }
                      }
                    }
                    
                    // Determine the correct section number
                    let sectionNumber;
                    if (section.key === 'auth') sectionNumber = 1;
                    else if (section.key === 'reputation') sectionNumber = 2;
                    else if (section.key === 'content') sectionNumber = 3;
                    else if (section.key === 'status') sectionNumber = 4;
                    else sectionNumber = idx + 1;
                    
                    // Calculate section health status based on symbols
                    let healthStatus = "Excellent";
                    let healthStatusColor = "#388e3c"; // Green for excellent
                    let hasErrors = false;
                    let hasWarnings = false;
                    
                    // Check for issues in the section
                    if (section.details && section.details.length > 0 && section.key !== 'status') {
                      // Look for critical errors (high scores) and warnings (moderate scores)
                      for (const detail of section.details) {
                        if (detail.score > 1.5) {
                          hasErrors = true;
                          break;
                        } else if (detail.score > 0) {
                          hasWarnings = true;
                        }
                      }
                      
                      // Set status based on findings
                      if (hasErrors) {
                        healthStatus = "Problèmes Critiques";
                        healthStatusColor = "#d32f2f"; // Red
                      } else if (hasWarnings) {
                        healthStatus = "Améliorations Recommandées";
                        healthStatusColor = "#ed6c02"; // Orange/warning
                      }
                    }
                    
                    return (
                      <Box key={section.key} mb={3} sx={{ border: '1px solid #eee', borderRadius: 2, p: 0, background: '#f9f9fc' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            cursor: 'pointer',
                            px: 2, py: 2,
                            borderRadius: 2,
                            background: isOpen ? '#e3f2fd' : 'inherit',
                            transition: 'background 0.2s',
                          }}
                          onClick={() => setOpenSection(isOpen ? null : section.key)}
                        >
                          <Typography variant="h6" sx={{ mb: 0, color: '#1976d2', fontWeight: 600 }}>
                            {(() => {
                              if (section.key === 'auth') return '1. ' + section.title;
                              if (section.key === 'reputation') return '2. ' + section.title;
                              if (section.key === 'content') return '3. ' + section.title;
                              if (section.key === 'status') return '4. ' + section.title;
                              return (idx + 1) + '. ' + section.title;
                            })()}
                          </Typography>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {section.key !== 'status' && (
                              <Typography variant="subtitle2" sx={{ 
                                mr: 2, 
                                fontWeight: 600, 
                                color: healthStatusColor 
                              }}>
                                {healthStatus}
                              </Typography>
                            )}
                            <span style={{ fontSize: 22 }}>{isOpen ? '▲' : '▼'}</span>
                          </div>
                        </Box>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 3, pb: 2, pt: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1, color: '#555' }}>{section.desc}</Typography>
                            {/* Display the LLM report content for this section */}
                            {sectionLLMReport && (
                              <Box sx={{ mb: 2, p: 2, background: '#f8fafd', borderRadius: 2 }}>
                                <div style={{ whiteSpace: 'pre-line', color: '#222', fontSize: 16 }}>
                                  {/* Remove score references from LLM report */}
                                  {sectionLLMReport.replace(/\(score \d+\.\d+\)/g, '')}
                                </div>
                              </Box>
                            )}
                            {/* Only show symbol details if there are no LLM reports */}
                            {section.details && section.details.length > 0 ? (
                              <Box component="ul" sx={{ pl: 3, mb: 1 }}>
                                {section.details.map((d, i) => (
                                  <li key={d.symbol + i} style={{ marginBottom: 4 }}>
                                    <span style={{ fontWeight: 600 }}>{d.symbol}</span>
                                    {/* Removed score display here */}
                                    {d.description && <span style={{ marginLeft: 8, color: '#888' }}>— {d.description}</span>}
                                    {d.options && d.options.length > 0 && (
                                      <span style={{ marginLeft: 8, color: '#aaa', fontSize: 12 }}>({d.options.join(' ')})</span>
                                    )}
                                  </li>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">Aucun symbole détecté dans cette section.</Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </Box>
              )}

              {/* Métadonnées */}
              <Box mt={3}>
                <Typography variant="caption" color="text.secondary">
                  Analyse effectuée le : {spamReport.timestamp ? new Date(spamReport.timestamp).toLocaleString('fr-FR') : ''} | Temps de scan : {spamReport.scanTimeMs} ms
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Source Code Modal */}
      <Modal open={modal === 'source'} onClose={() => setModal(null)}>
        <Box sx={{ position: 'relative', backgroundColor: 'white', borderRadius: 2, p: 4, maxWidth: '80vw', margin: '5vh auto', outline: 'none', maxHeight: '80vh', overflowY: 'auto' }}>
          <IconButton
            aria-label="close"
            onClick={() => setModal(null)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ pr: '40px' }}> {/* Add padding to the right to avoid overlap with close button */}
            <Typography variant="h6">{sourceTitle}</Typography>
            <Button
              variant="contained"
              startIcon={copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
              onClick={handleCopy}
              disabled={copySuccess}
              color={copySuccess ? 'success' : 'primary'}
            >
              {copySuccess ? 'Copié !' : 'Copier'}
            </Button>
          </Box>
          <TextareaAutosize
            value={sourceContent}
            readOnly
            minRows={15}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: 14, border: '1px solid #ccc', borderRadius: 4, padding: 10 }}
          />
        </Box>
      </Modal>

      {/* Email Preview Modal using MailDev */}
      <Modal open={mailPreviewModalOpen} onClose={() => setMailPreviewModalOpen(false)} fullWidth maxWidth="lg">
        <Box sx={{ p: 3, height: '90vh', display: 'flex', flexDirection: 'column' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Aperçu Email on Acid</Typography>
            <IconButton onClick={() => setMailPreviewModalOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {previewError && <Alert severity="error" sx={{ mt: 2 }}>{previewError}</Alert>}

          {(isPreviewLoading || isPolling) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexGrow: 1 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>
                {isPolling ? `Génération des aperçus en cours... (Test ID: ${testId})` : 'Soumission du test...'}
              </Typography>
            </Box>
          )}

          {!isPreviewLoading && !isPolling && previewResults.length > 0 && (
            <Grid container spacing={2} sx={{ mt: 2, overflowY: 'auto' }}>
              {previewResults.map((result) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={result.id}>
                  <Paper 
                    elevation={3} 
                    sx={{ cursor: 'pointer', '&:hover': { boxShadow: 6 } }} 
                    onClick={() => setSelectedClient(result)}
                  >
                    <Box sx={{ height: 200, overflow: 'hidden', borderTopLeftRadius: 4, borderTopRightRadius: 4 }}>
                      {result.status === 'Complete' && result.thumbnail_url ? (
                        <img src={result.thumbnail_url} alt={result.name} style={{ width: '100%', height: 'auto' }} />
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                          <CircularProgress size={30} />
                          <Typography variant="caption" sx={{ mt: 1 }}>{result.status}...</Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ p: 1.5, textAlign: 'center', fontWeight: 'medium' }}>
                      {result.name}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Modal>

      {/* Full-size image viewer modal */}
      <Modal open={!!selectedClient} onClose={() => setSelectedClient(null)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ position: 'relative', outline: 'none' }}>
          <IconButton onClick={() => setSelectedClient(null)} sx={{ position: 'absolute', top: -40, right: -40, color: 'white', bgcolor: 'rgba(0,0,0,0.5)' }}>
            <CloseIcon />
          </IconButton>
          <img src={selectedClient?.image_url} alt={selectedClient?.name} style={{ maxHeight: '90vh', maxWidth: '90vw' }} />
        </Box>
      </Modal>

      {/* Send Test Dialog */}
      <Dialog open={isSendTestModalOpen} onClose={() => setIsSendTestModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Envoyer un test
          <IconButton
            aria-label="close"
            onClick={() => setIsSendTestModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>Destinataires *</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {testRecipients.map((email, index) => (
              <Chip
                key={index}
                label={email}
                onDelete={() => setTestRecipients(testRecipients.filter((_, i) => i !== index))}
              />
            ))}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Ajouter un destinataire"
              type="email"
              fullWidth
              variant="outlined"
              value={recipientInput}
              onChange={(e) => setRecipientInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && recipientInput) {
                  e.preventDefault();
                  if (recipientInput && !testRecipients.includes(recipientInput)) {
                    setTestRecipients([...testRecipients, recipientInput]);
                  }
                  setRecipientInput('');
                }
              }}
            />
            <Tooltip title="Ajouter le destinataire">
              <span>
                <IconButton
                  color="primary"
                  onClick={() => {
                    if (recipientInput && !testRecipients.includes(recipientInput)) {
                      setTestRecipients([...testRecipients, recipientInput]);
                    }
                    setRecipientInput('');
                  }}
                  disabled={!recipientInput}
                  sx={{ mt: '8px' }}
                >
                  <AddCircleOutlineIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          
          <Typography gutterBottom sx={{ mt: 2 }}>Rechercher un contact</Typography>
          <Autocomplete
            freeSolo
            options={contactOptions}
            loading={contactsLoading}
            getOptionLabel={(option) => typeof option === 'string' ? option : `${option.properties.firstName || ''} ${option.properties.lastName || ''} <${option.properties.email}>`}
            onInputChange={(event, newInputValue) => {
              setContactInput(newInputValue);
              debouncedSearch(newInputValue);
            }}
            onChange={(event, newValue) => {
              if (typeof newValue !== 'string' && newValue?.properties?.email) {
                if (!testRecipients.includes(newValue.properties.email)) {
                  setTestRecipients([...testRecipients, newValue.properties.email]);
                }
                setContactInput(''); // Clear input after selection
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Rechercher par nom, prénom ou email..."
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {contactsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSendTestModalOpen(false)}>
            Annuler
          </Button>
          <Button
            onClick={() => {
              setSendSuccess(true);
              setTimeout(() => {
                setSendSuccess(false);
                setIsSendTestModalOpen(false);
              }, 2000);
            }}
            variant="contained"
            endIcon={<SendIcon />}
            sx={{ ml: 1 }}
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// Composant utilitaire pour afficher le texte LLM sans markdown, titres en gras
function LLMReportText({ text }) {
  if (!text) return null;
  // Nettoyage basique :
  // - Supprimer les astérisques multiples (****, **, *)
  // - Remplacer les titres markdown (##, #) par <b>
  // - Remplacer les retours à la ligne multiples par <br/>
  let clean = text
    .replace(/\*{2,}/g, '')
    .replace(/^\s*#+\s*(.+)$/gm, '<b>$1</b>')
    .replace(/\n{2,}/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
  // Optionnel : mettre en gras les titres de section (ex : SECTION X : ...)
  clean = clean.replace(/(SECTION \d+ : [^<\n]+)/g, '<b>$1</b>');
  return <div style={{ fontSize: 16, color: '#222' }} dangerouslySetInnerHTML={{ __html: clean }} />;
}

export default EmailPreviewPage;
